// ===================================
// TRENCH QUEST - Trading Engine
// Handles player trades with skill-based mechanics
// ===================================

import type { TokenHolding, Trade, Portfolio, PlayerTraits } from '@/types'
import { type MarketState, executeTrade, getPriceInUSDC } from '../market/marketEngine'
import { getToken } from '../tokens/data'

export interface TradeResult {
  success: boolean
  trade?: Trade
  newPortfolio: Portfolio
  newMarket: MarketState
  message: string
  degenQuote: string // Funny commentary
}

export interface TradingSkillCheck {
  type: 'timing' | 'size' | 'exit' | 'entry'
  difficulty: number // 0-1
  passed: boolean
  bonus: number // Multiplier adjustment
}

// Generate trade ID
const generateTradeId = (): string => {
  return `trade_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Skill-based timing check (affects slippage)
const performTimingCheck = (
  traits: PlayerTraits,
  volatility: number
): TradingSkillCheck => {
  // Higher alpha score = better timing
  const skillBonus = traits.alphaScore / 200 // Max 0.5 bonus
  const roll = Math.random()
  const threshold = 0.5 - skillBonus + volatility * 0.3

  const passed = roll > threshold
  const bonus = passed ? 1 + (roll - threshold) * 0.2 : 1 - (threshold - roll) * 0.1

  return {
    type: 'timing',
    difficulty: volatility,
    passed,
    bonus: Math.max(0.8, Math.min(1.3, bonus)),
  }
}

// Check if trade size is appropriate (affects outcome)
const performSizeCheck = (
  tradeValue: number,
  portfolioValue: number,
  traits: PlayerTraits
): TradingSkillCheck => {
  const percentage = tradeValue / portfolioValue
  const degenThreshold = 0.2 + (traits.degenScore / 100) * 0.3 // Higher degen = willing to risk more

  // Very small trades always pass
  if (percentage < 0.05) {
    return { type: 'size', difficulty: 0.1, passed: true, bonus: 1.0 }
  }

  // Check if size is appropriate
  const passed = percentage < degenThreshold || Math.random() < 0.3 // 30% chance to pass even if oversized
  const bonus = passed ? 1.0 : 0.95 // Small penalty for oversized trades

  return {
    type: 'size',
    difficulty: percentage,
    passed,
    bonus,
  }
}

// Execute a buy order
export const executeBuyOrder = (
  portfolio: Portfolio,
  market: MarketState,
  traits: PlayerTraits,
  toToken: string,
  usdcAmount: number
): TradeResult => {
  // Validate USDC balance
  const usdcHolding = portfolio.holdings.find(h => h.symbol === 'USDC')
  if (!usdcHolding || usdcHolding.amount < usdcAmount) {
    return {
      success: false,
      newPortfolio: portfolio,
      newMarket: market,
      message: 'Insufficient USDC balance',
      degenQuote: "Ser, you can't buy what you can't afford. This isn't 2021 leverage trading.",
    }
  }

  // Can't buy USDC with USDC
  if (toToken === 'USDC') {
    return {
      success: false,
      newPortfolio: portfolio,
      newMarket: market,
      message: 'Cannot buy USDC with USDC',
      degenQuote: 'Big brain move trying to buy USDC with USDC. Infinite money glitch?',
    }
  }

  // Check if token is rugged
  if (market.ruggedTokens.includes(toToken)) {
    return {
      success: false,
      newPortfolio: portfolio,
      newMarket: market,
      message: 'Token has been rugged',
      degenQuote: `${toToken} got rugged harder than your hopes and dreams. Move on, anon.`,
    }
  }

  const tokenData = getToken(toToken)
  if (!tokenData) {
    return {
      success: false,
      newPortfolio: portfolio,
      newMarket: market,
      message: 'Token not found',
      degenQuote: "That token doesn't exist. Yet. Maybe launch it yourself?",
    }
  }

  // Skill checks
  const timingCheck = performTimingCheck(traits, tokenData.volatility)
  const sizeCheck = performSizeCheck(usdcAmount, portfolio.totalValue, traits)

  // Calculate slippage with skill bonuses
  const baseSlippage = 0.01
  const adjustedSlippage = baseSlippage / (timingCheck.bonus * sizeCheck.bonus)

  // Execute the trade
  const { toAmount, executionPrice, slippage, newMarket } = executeTrade(
    market,
    'USDC',
    toToken,
    usdcAmount,
    adjustedSlippage
  )

  // Update portfolio
  const newHoldings: TokenHolding[] = [...portfolio.holdings]

  // Reduce USDC
  const usdcIndex = newHoldings.findIndex(h => h.symbol === 'USDC')
  if (usdcIndex >= 0) {
    const currentUsdc = newHoldings[usdcIndex]
    if (currentUsdc) {
      newHoldings[usdcIndex] = {
        ...currentUsdc,
        amount: currentUsdc.amount - usdcAmount,
      }
    }
  }

  // Add or update token holding
  const existingIndex = newHoldings.findIndex(h => h.symbol === toToken)
  if (existingIndex >= 0) {
    const existing = newHoldings[existingIndex]
    if (existing) {
      const newAmount = existing.amount + toAmount
      const newAvgPrice = (existing.avgBuyPrice * existing.amount + executionPrice * toAmount) / newAmount
      newHoldings[existingIndex] = {
        symbol: existing.symbol,
        name: existing.name,
        amount: newAmount,
        avgBuyPrice: newAvgPrice,
        currentPrice: executionPrice,
      }
    }
  } else {
    newHoldings.push({
      symbol: toToken,
      name: tokenData.name,
      amount: toAmount,
      avgBuyPrice: executionPrice,
      currentPrice: executionPrice,
    })
  }

  // Calculate new total value
  const totalValue = newHoldings.reduce((sum, h) => {
    const price = getPriceInUSDC(newMarket, h.symbol)
    return sum + h.amount * price
  }, 0)

  const newPortfolio: Portfolio = {
    holdings: newHoldings,
    cashBalance: newHoldings.find(h => h.symbol === 'USDC')?.amount || 0,
    totalValue,
  }

  // Create trade record
  const trade: Trade = {
    id: generateTradeId(),
    type: 'buy',
    fromToken: 'USDC',
    toToken,
    fromAmount: usdcAmount,
    toAmount,
    price: executionPrice,
    timestamp: new Date(),
    narrative: generateTradeNarrative('buy', toToken, slippage, traits),
  }

  return {
    success: true,
    trade,
    newPortfolio,
    newMarket,
    message: `Bought ${toAmount.toFixed(4)} ${toToken} for ${usdcAmount.toFixed(2)} USDC`,
    degenQuote: getBuyQuote(toToken, tokenData.category, timingCheck.passed),
  }
}

// Execute a sell order
export const executeSellOrder = (
  portfolio: Portfolio,
  market: MarketState,
  traits: PlayerTraits,
  fromToken: string,
  amount: number
): TradeResult => {
  // Can't sell USDC
  if (fromToken === 'USDC') {
    return {
      success: false,
      newPortfolio: portfolio,
      newMarket: market,
      message: 'Cannot sell USDC',
      degenQuote: 'Selling USDC? What are you, some kind of stablecoin shorter? Based if true.',
    }
  }

  // Validate holding
  const holding = portfolio.holdings.find(h => h.symbol === fromToken)
  if (!holding || holding.amount < amount) {
    return {
      success: false,
      newPortfolio: portfolio,
      newMarket: market,
      message: 'Insufficient token balance',
      degenQuote: `You don't have enough ${fromToken}. Did you already paper hand it?`,
    }
  }

  const tokenData = getToken(fromToken)
  if (!tokenData) {
    return {
      success: false,
      newPortfolio: portfolio,
      newMarket: market,
      message: 'Token not found',
      degenQuote: 'Error 404: Token not found. Just like your gains.',
    }
  }

  // Check if rugged
  if (market.ruggedTokens.includes(fromToken)) {
    return {
      success: false,
      newPortfolio: portfolio,
      newMarket: market,
      message: 'Token has been rugged - no liquidity',
      degenQuote: `${fromToken} is rugged. That bag is worth exactly $0.00. F in chat.`,
    }
  }

  // Skill checks
  const timingCheck = performTimingCheck(traits, tokenData.volatility)
  const tradeValue = amount * getPriceInUSDC(market, fromToken)
  const sizeCheck = performSizeCheck(tradeValue, portfolio.totalValue, traits)

  // Calculate slippage
  const baseSlippage = 0.01
  const adjustedSlippage = baseSlippage / (timingCheck.bonus * sizeCheck.bonus)

  // Execute the trade
  const { toAmount, executionPrice, slippage, newMarket } = executeTrade(
    market,
    fromToken,
    'USDC',
    amount,
    adjustedSlippage
  )

  // Update portfolio
  const newHoldings: TokenHolding[] = [...portfolio.holdings]

  // Reduce token holding
  const tokenIndex = newHoldings.findIndex(h => h.symbol === fromToken)
  if (tokenIndex >= 0) {
    const currentToken = newHoldings[tokenIndex]
    if (currentToken) {
      if (currentToken.amount - amount < 0.000001) {
        // Remove holding entirely if dust
        newHoldings.splice(tokenIndex, 1)
      } else {
        newHoldings[tokenIndex] = {
          ...currentToken,
          amount: currentToken.amount - amount,
        }
      }
    }
  }

  // Add USDC (or create USDC holding if none exists)
  const usdcIndex = newHoldings.findIndex(h => h.symbol === 'USDC')
  if (usdcIndex >= 0) {
    const currentUsdc = newHoldings[usdcIndex]
    if (currentUsdc) {
      newHoldings[usdcIndex] = {
        ...currentUsdc,
        amount: currentUsdc.amount + toAmount,
      }
    }
  } else {
    // No USDC holding exists - create one
    newHoldings.push({
      symbol: 'USDC',
      name: 'USD Coin',
      amount: toAmount,
      avgBuyPrice: 1,
      currentPrice: 1,
    })
  }

  // Calculate P&L
  const costBasis = holding.avgBuyPrice * amount
  const proceeds = toAmount
  const pnl = proceeds - costBasis
  const pnlPercentage = costBasis > 0 ? (pnl / costBasis) * 100 : 0

  // Calculate new total value
  const totalValue = newHoldings.reduce((sum, h) => {
    const price = getPriceInUSDC(newMarket, h.symbol)
    return sum + h.amount * price
  }, 0)

  const newPortfolio: Portfolio = {
    holdings: newHoldings,
    cashBalance: newHoldings.find(h => h.symbol === 'USDC')?.amount || 0,
    totalValue,
  }

  // Create trade record
  const trade: Trade = {
    id: generateTradeId(),
    type: 'sell',
    fromToken,
    toToken: 'USDC',
    fromAmount: amount,
    toAmount,
    price: executionPrice,
    timestamp: new Date(),
    narrative: generateTradeNarrative('sell', fromToken, slippage, traits, pnl),
  }

  return {
    success: true,
    trade,
    newPortfolio,
    newMarket,
    message: `Sold ${amount.toFixed(4)} ${fromToken} for ${toAmount.toFixed(2)} USDC (${pnl >= 0 ? '+' : ''}${pnlPercentage.toFixed(2)}%)`,
    degenQuote: getSellQuote(fromToken, pnl, pnlPercentage, timingCheck.passed),
  }
}

// Generate narrative for trade
const generateTradeNarrative = (
  type: 'buy' | 'sell',
  token: string,
  slippage: number,
  traits: PlayerTraits,
  pnl?: number
): string => {
  const slippageComment = slippage > 0.02 ? ' Slippage was rough.' : ''

  if (type === 'buy') {
    if (traits.degenScore > 70) {
      return `Aped into ${token} with zero hesitation.${slippageComment} This is the way.`
    } else if (traits.degenScore > 40) {
      return `Entered a position in ${token}.${slippageComment} Risk calculated.`
    } else {
      return `Carefully acquired some ${token}.${slippageComment} Playing it safe.`
    }
  } else {
    if (pnl && pnl > 0) {
      return `Took profits on ${token}.${slippageComment} Realized gains are the only gains.`
    } else if (pnl && pnl < 0) {
      return `Cut losses on ${token}.${slippageComment} Sometimes you gotta take the L.`
    } else {
      return `Exited ${token} position.${slippageComment} Moving to different plays.`
    }
  }
}

// Degen buy quotes
const getBuyQuote = (token: string, category: string, goodTiming: boolean): string => {
  const goodTimingQuotes = [
    `Caught that ${token} dip perfectly. You might actually make it.`,
    `Nice entry on ${token}. Your alpha score just increased.`,
    `Solid buy. ${token} chart looking bullish. Or you're coping. Either way, LFG.`,
    `${token} acquired. Now pray to the Solana gods.`,
  ]

  const badTimingQuotes = [
    `Bought ${token} at local top. Classic move, anon.`,
    `${token} purchased. Immediately -2%. This is crypto.`,
    `Congratulations, you're now a ${token} bagholder. Welcome to the club.`,
    `${token} buy executed. Chart goes down. Many such cases.`,
  ]

  const degenQuotes = [
    `YOLO'd into ${token}. Lambo or food stamps, no in between.`,
    `${token} ape successful. Your portfolio is now 90% meme coins.`,
    `Bought ${token} because someone on CT said to. WAGMI... right?`,
    `${token} secured. This is either generational wealth or generational stupidity.`,
  ]

  const blueChipQuotes = [
    `Added ${token}. Look at you being responsible.`,
    `${token} buy executed. Your dad would be proud. If he understood crypto.`,
    `Solid ${token} accumulation. This is the boring way to make money.`,
  ]

  let quotes: string[]
  if (category === 'ultra_degen' || category === 'degen') {
    quotes = degenQuotes
  } else if (category === 'blue_chip') {
    quotes = blueChipQuotes
  } else if (goodTiming) {
    quotes = goodTimingQuotes
  } else {
    quotes = badTimingQuotes
  }

  const index = Math.floor(Math.random() * quotes.length)
  return quotes[index] || quotes[0] || 'Trade executed.'
}

// Degen sell quotes
const getSellQuote = (token: string, pnl: number, percentage: number, _goodTiming: boolean): string => {
  const bigWinQuotes = [
    `${percentage.toFixed(0)}% gain on ${token}. You are actually HIM.`,
    `Secured the bag on ${token}. +${percentage.toFixed(0)}%. Screenshot this.`,
    `${token} play printed. You're the alpha now.`,
    `Closed ${token} for massive profit. Your ancestors are smiling upon you.`,
  ]

  const smallWinQuotes = [
    `Profit is profit. Even if ${percentage.toFixed(0)}% feels mid.`,
    `Green exit on ${token}. Could've been more but could've been zero.`,
    `Sold ${token} in profit. Paper hands? Nah, smart hands.`,
    `${token} sold. +${percentage.toFixed(0)}%. Mid but better than rekt.`,
  ]

  const smallLossQuotes = [
    `Small L on ${token}. It happens. Touch grass and come back.`,
    `${token} stop loss hit. -${Math.abs(percentage).toFixed(0)}%. At least you had a stop loss.`,
    `Cut ${token} for minor loss. Discipline > copium.`,
    `Exited ${token}. -${Math.abs(percentage).toFixed(0)}%. The market will provide other opportunities.`,
  ]

  const bigLossQuotes = [
    `${token} absolutely destroyed you. -${Math.abs(percentage).toFixed(0)}%. F.`,
    `Took the L on ${token}. Your portfolio will never forget this.`,
    `Sold ${token} at massive loss. This is the way... to poverty.`,
    `${token} trade went poorly. Very poorly. -${Math.abs(percentage).toFixed(0)}%. NGMI energy.`,
  ]

  let quotes: string[]
  if (pnl > 0 && percentage > 100) {
    quotes = bigWinQuotes
  } else if (pnl > 0) {
    quotes = smallWinQuotes
  } else if (percentage > -20) {
    quotes = smallLossQuotes
  } else {
    quotes = bigLossQuotes
  }

  const index = Math.floor(Math.random() * quotes.length)
  return quotes[index] || quotes[0] || 'Trade executed.'
}

// Calculate unrealized P&L for a holding
export const calculateUnrealizedPnL = (
  holding: TokenHolding,
  market: MarketState
): { pnl: number; percentage: number } => {
  const currentPrice = getPriceInUSDC(market, holding.symbol)
  const currentValue = holding.amount * currentPrice
  const costBasis = holding.amount * holding.avgBuyPrice
  const pnl = currentValue - costBasis
  const percentage = costBasis > 0 ? (pnl / costBasis) * 100 : 0

  return { pnl, percentage }
}

// Get best and worst trades from history
export const analyzeTrades = (trades: Trade[]): {
  bestTrade: Trade | null
  worstTrade: Trade | null
  totalPnL: number
  winRate: number
} => {
  const sells = trades.filter(t => t.type === 'sell')
  let bestTrade: Trade | null = null
  let worstTrade: Trade | null = null
  let bestPnL = -Infinity
  let worstPnL = Infinity
  let wins = 0
  let totalPnL = 0

  for (const trade of sells) {
    // This is simplified - in reality we'd track cost basis per trade
    const pnl = trade.toAmount - trade.fromAmount * trade.price
    totalPnL += pnl

    if (pnl > 0) wins++
    if (pnl > bestPnL) {
      bestPnL = pnl
      bestTrade = trade
    }
    if (pnl < worstPnL) {
      worstPnL = pnl
      worstTrade = trade
    }
  }

  return {
    bestTrade,
    worstTrade,
    totalPnL,
    winRate: sells.length > 0 ? (wins / sells.length) * 100 : 0,
  }
}
